import axiosNest from '../../api/axiosNest';
import { menuLabel } from '../menuConfig';
import { ROLES } from '../users/role';
import type { CsvColumn } from '../../shared/csvExport';
import type { UsageActionStat, UsageClickStat, UsageInteractionStat } from './usageTrace';

/**
 * Pomiar użycia — odczyt raportu (`GET /usage-events/report`, tylko ADMIN;
 * decyzja użytkownika 22.10.2026, S-22.10-02, przełącznik `usageTelemetry`).
 * Kształt jak w API (`usage-events.service.ts`, `UsageReport`).
 */

/** Klucz przełącznika (S-22.10-02). Dane — nie przemianowujemy. */
export const FEATURE_USAGE_TELEMETRY = 'usageTelemetry';
/** Rola, która widzi ekran — jak `AdminOnlyGuard` w API. */
export const USAGE_REPORT_ROLE = 'ADMIN';

export interface PathStepStat {
  nr: number;
  measurable: boolean;
  reached: number;
  stopped: number;
}

export interface PathFunnel {
  id: string;
  status: string | null;
  priority: string | null;
  measurableSteps: number;
  started: number;
  completed: number;
  startedUsers: number;
  completedUsers: number;
  departments: string[];
  medianDurationSec: number | null;
  steps: PathStepStat[];
}

export interface UsageReport {
  period: { from: string; to: string };
  filters: { department: string | null; role: string | null; userId: number | null; environment: string | null };
  options: {
    departments: string[];
    roles: string[];
    environments: string[];
    users: { id: number; username: string | null }[];
  };
  totals: {
    events: number;
    users: number;
    screens: number;
    clicks: number;
    dialogConfirms: number;
    apiErrors: number;
    actions: number;
    dialogs: number;
    filters: number;
    exports: number;
  };
  contractVersion: string;
  paths: PathFunnel[];
  pathsTruncated: boolean;
  topScreens: {
    route: string;
    entries: number;
    users: number;
    totalTimeSec: number | null;
    medianTimeSec: number | null;
  }[];
  topElements: { elementId: string; route: string | null; count: number; users: number; contract: boolean }[];
  apiErrors: { route: string | null; statusCode: number; apiEndpoint: string | null; count: number; users: number }[];
  dailyActivity: { day: string; department: string | null; events: number; users: number }[];
  /** Pełny ślad działań (23.10.2026). */
  topActions: UsageActionStat[];
  topClicks: UsageClickStat[];
  interactions: UsageInteractionStat[];
  retentionDays: number;
  environment: string;
}

export interface UsageQuery {
  from: string;
  to: string;
  department?: string;
  role?: string;
  userId?: string;
  environment?: string;
}

/**
 * „Usuń dane starsze niż…” (decyzja użytkownika 23.10.2026: „trzymamy, aż
 * usunę”) — podgląd liczby i usunięcie wszystkich zdarzeń sprzed początku
 * podanego dnia polskiego. Tylko ADMIN; powód obowiązkowy, wpis w dzienniku
 * działań robi API.
 */
export async function fetchPurgePreview(before: string): Promise<{ before: string; count: number }> {
  const res = await axiosNest.get<{ before: string; count: number }>('/usage-events/purge-preview', {
    params: { before },
  });
  return res.data;
}

export async function purgeUsageEvents(
  before: string,
  reason: string,
): Promise<{ before: string; removed: number }> {
  const res = await axiosNest.post<{ before: string; removed: number }>('/usage-events/purge', {
    before,
    reason,
  });
  return res.data;
}

export async function fetchUsageReport(q: UsageQuery): Promise<UsageReport> {
  const params: Record<string, string> = { from: q.from, to: q.to };
  for (const k of ['department', 'role', 'userId', 'environment'] as const) {
    if (q[k]) params[k] = q[k] as string;
  }
  const res = await axiosNest.get<UsageReport>('/usage-events/report', { params });
  return res.data;
}

// ── Teksty dla operatora ──────────────────────────────────────────────

export const INTRO_NOTE =
  'Pomiar pokazuje, jak pracownicy korzystają z aplikacji: wejścia na ekrany i czas na nich, kliknięcia przycisków, odnośników, zakładek, pozycji menu, pól wyboru i wierszy list (ze stałym napisem z ekranu), działania zmieniające dane z kodem odpowiedzi, okna, filtry (sama nazwa), eksport, wylogowania i błędy. Nie zapisujemy treści pól, wartości filtrów, treści komórek, nazw kontrahentów i kierowców ani innych danych osobowych — tylko numer konta, rolę i dział.';
export const CONSENT_NOTE =
  'To pomiar pracy pracowników klienta. Przed pilotażem trzeba poinformować użytkowników, że pomiar działa i w jakim celu (do ustalenia z Marathonem).';
export const FEATURE_OFF_NOTE = 'Pomiar użycia jest wyłączony w wersji aplikacji, z której korzystasz.';
export const ADMIN_ONLY_NOTE = 'Pomiar użycia widzi wyłącznie administrator.';
export const LOAD_ERROR = 'Nie udało się wczytać pomiaru. Odśwież stronę albo zmień filtry.';

/** Retencja 0 = bez limitu (decyzja użytkownika 23.10.2026). */
export const UNLIMITED_RETENTION = 0;

/** Zdanie o przechowywaniu zdarzeń w opisie ekranu. */
export function retentionText(days: number): string {
  return days === UNLIMITED_RETENTION
    ? 'Zdarzenia przechowujemy bez limitu — nie usuwamy ich automatycznie; starsze dane usuwasz sam przyciskiem „Usuń dane starsze niż…”.'
    : `Zdarzenia przechowujemy ${days} dni — starsze usuwa codzienne czyszczenie (parametr „Pomiar użycia: przechowywanie zdarzeń”).`;
}

export const PURGE_BUTTON = 'Usuń dane starsze niż…';
export const PURGE_TITLE = 'Usuń dane pomiaru starsze niż wybrany dzień';
export const PURGE_NOTE =
  'Usuwa wszystkie zapisane zdarzenia sprzed początku wybranego dnia — z całego pomiaru, nie tylko z filtrów na ekranie. Tego nie da się cofnąć. Usunięcie trafia do dziennika działań z Twoim powodem.';
export const PURGE_REASON_REQUIRED = 'Podaj powód usunięcia';
export const PURGE_ERROR = 'Nie udało się usunąć danych pomiaru.';

/** Data RRRR-MM-DD jako DD.MM.RRRR. */
export const dayPl = (day: string) => day.split('-').reverse().join('.');

/** Komunikat po usunięciu. */
export const purgeDoneText = (removed: number, before: string) =>
  `Usunięto ${removed.toLocaleString('pl-PL')} zdarzeń sprzed ${dayPl(before)}.`;
export const PATHS_NOTE =
  'Ścieżki z paczki testów: przejście to jedno zlecenie (prezlecenie z wierszem banku liczy się jako jego zlecenie) przez kolejne działy, a kroki to identyfikatory elementów z kontraktu. Krok bez odpowiednika w aplikacji jest pomijany. „Zatrzymanie” to ostatni krok przejścia, które nie doszło do końca.';

export const DEPARTMENT_LABELS: Readonly<Record<string, string>> = {
  FTL: 'FTL',
  TRADE: 'Trade',
  CS: 'CS',
  ADMINISTRATION: 'Administracja',
  ADMIN: 'Administratorzy',
  ALL: 'Wszystkie działy (rola „Użytkownik”)',
  GUEST: 'Goście',
};

export const ENVIRONMENT_LABELS: Readonly<Record<string, string>> = {
  dev: 'deweloperskie',
  staging: 'akceptacyjne',
  prod: 'produkcja',
  local: 'lokalne',
  unknown: 'nieznane',
};

export const departmentLabel = (d: string | null | undefined) =>
  d ? (DEPARTMENT_LABELS[d] ?? d) : 'bez działu';
export const roleLabel = (r: string | null | undefined) =>
  r ? (ROLES.find((x) => x.id === r)?.name ?? r) : '—';
export const environmentLabel = (e: string | null | undefined) =>
  e ? (ENVIRONMENT_LABELS[e] ?? e) : '—';
export const userLabel = (u: { id: number; username: string | null }) =>
  u.username ? `${u.username} (nr ${u.id})` : `konto nr ${u.id}`;

/** Co oznacza końcówka trasy po zasobie. */
const ROUTE_SUFFIX: Readonly<Record<string, string>> = {
  ':id/show': 'karta',
  ':id': 'edycja',
  create: 'nowe',
  ':id/copy': 'kopia',
  ':id/control': 'okno kontroli',
  'my-fleet': 'moja flota',
};

/** Nazwa ekranu z wzorca trasy: nazwa z menu i rodzaj widoku. */
export function routeLabel(route: string | null | undefined): string {
  if (!route) return '—';
  if (route === '/') return 'Pulpit';
  const [resource, ...rest] = route.replace(/^\//, '').split('/');
  let name: string;
  try {
    name = menuLabel(resource);
  } catch {
    name = resource;
  }
  if (rest.length === 0) return name;
  const suffix = ROUTE_SUFFIX[rest.join('/')];
  return `${name} — ${suffix ?? rest.join('/')}`;
}

/** Czas przejścia: „45 s”, „12 min”, „2 h 05 min”, „3 dni 4 h”. */
export function durationText(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec)) return '—';
  const s = Math.round(sec);
  if (s < 60) return `${s} s`;
  const min = Math.round(s / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return `${h} h ${String(m).padStart(2, '0')} min`;
  const d = Math.floor(h / 24);
  return `${d} ${d === 1 ? 'dzień' : 'dni'} ${h % 24} h`;
}

/** Gdzie się zatrzymują: „krok 3: 4, krok 5: 1”. */
export function stopsText(p: PathFunnel): string {
  const stops = p.steps.filter((s) => s.stopped > 0).map((s) => `krok ${s.nr}: ${s.stopped}`);
  return stops.length ? stops.join(', ') : '—';
}

/** Kroki niemierzalne: „2, 4” albo „—”. */
export function unmeasuredText(p: PathFunnel): string {
  const list = p.steps.filter((s) => !s.measurable).map((s) => String(s.nr));
  return list.length ? list.join(', ') : '—';
}

// ── Eksport CSV (kolumny po polsku, jak na ekranie) ──────────────────

type Rec = Record<string, unknown>;

export const PATH_CSV: readonly CsvColumn[] = [
  { header: 'Ścieżka', value: (r) => (r as Rec).id },
  { header: 'Priorytet', value: (r) => (r as Rec).priority },
  { header: 'Status w rejestrze', value: (r) => (r as Rec).status },
  { header: 'Kroki mierzalne', value: (r) => (r as Rec).measurableSteps },
  { header: 'Kroki bez pomiaru', value: (r) => unmeasuredText(r as unknown as PathFunnel) },
  { header: 'Zaczęte przejścia', value: (r) => (r as Rec).started },
  { header: 'Użytkownicy (zaczęte)', value: (r) => (r as Rec).startedUsers },
  { header: 'Ukończone przejścia', value: (r) => (r as Rec).completed },
  { header: 'Użytkownicy (ukończone)', value: (r) => (r as Rec).completedUsers },
  { header: 'Zatrzymania', value: (r) => stopsText(r as unknown as PathFunnel) },
  { header: 'Mediana czasu przejścia (s)', value: (r) => (r as Rec).medianDurationSec },
  { header: 'Działy', value: (r) => ((r as Rec).departments as string[]).map(departmentLabel).join(', ') },
];

export const SCREEN_CSV: readonly CsvColumn[] = [
  { header: 'Ekran', value: (r) => routeLabel((r as Rec).route as string) },
  { header: 'Trasa', value: (r) => (r as Rec).route },
  { header: 'Wejścia', value: (r) => (r as Rec).entries },
  { header: 'Użytkownicy', value: (r) => (r as Rec).users },
  { header: 'Łączny czas (s)', value: (r) => (r as Rec).totalTimeSec },
  { header: 'Mediana czasu (s)', value: (r) => (r as Rec).medianTimeSec },
];

export const ELEMENT_CSV: readonly CsvColumn[] = [
  { header: 'Element', value: (r) => (r as Rec).elementId },
  { header: 'Z kontraktu', value: (r) => (r as Rec).contract },
  { header: 'Najczęściej na ekranie', value: (r) => routeLabel((r as Rec).route as string | null) },
  { header: 'Kliknięcia', value: (r) => (r as Rec).count },
  { header: 'Użytkownicy', value: (r) => (r as Rec).users },
];

export const ERROR_CSV: readonly CsvColumn[] = [
  { header: 'Ekran', value: (r) => routeLabel((r as Rec).route as string | null) },
  { header: 'Kod', value: (r) => (r as Rec).statusCode },
  { header: 'Zapytanie', value: (r) => (r as Rec).apiEndpoint },
  { header: 'Liczba', value: (r) => (r as Rec).count },
  { header: 'Użytkownicy', value: (r) => (r as Rec).users },
];

export const DAILY_CSV: readonly CsvColumn[] = [
  { header: 'Dzień', value: (r) => (r as Rec).day },
  { header: 'Dział', value: (r) => departmentLabel((r as Rec).department as string | null) },
  { header: 'Zdarzenia', value: (r) => (r as Rec).events },
  { header: 'Użytkownicy', value: (r) => (r as Rec).users },
];
